import React, { useState, forwardRef, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
// @mui
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { Box, Grid, Typography } from '@mui/material';
import { LiveChatWidget } from '@livechat/widget-react';
import ReactPixel from 'react-facebook-pixel';
// hooks
import useSettings from '../../hooks/useSettings';
import useResponsive from '../../hooks/useResponsive';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
// config
import { HEADER, NAVBAR } from '../../config';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import useAuth from '../../hooks/useAuth';

//
import DashboardHeader from './header';
import NavbarVertical from './navbar/NavbarVertical';
import NavbarHorizontal from './navbar/NavbarHorizontal';
import Footer from './Footer';

import Image from '../../components/Image';
import palette from '../../theme/palette';

import { getGameCategsAndCompanies, getSiteSettings, getUserMemberBalance } from '../../redux/slices/lookup';

import useLocales from '../../hooks/useLocales';

import { openTransactionsDialog } from '../../redux/slices/transactionsDialog';

import { setGameCategories, setActiveTab } from '../../redux/slices/game';

import conObj from '../../utils/connection-assistant';

import { usePageVisibility } from '../../hooks/usePageVisibility';
import WithdrawFormDialog from './header/withdraw/WithdrawFormDialog';
import PopupBanner from './PopupBanner';
import SubHeaderNav from '../../views/game/SubHeaderNav';
import { openLoginSignup } from '../../redux/slices/authPages';
import TransactionDialog from './header/TransactionDialog';
import { closeLoadingScreen, openLoadingScreen } from '../../redux/slices/settings';
import { openAddMemberBankDialog } from '../../redux/slices/addMemberBankDialog';
import SpinWheelButton from '../../views/promotion/SpinWheelButton';

// ----------------------------------------------------------------------

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})(({ isAuthenticated, collapseClick, theme }) => ({
  flexGrow: 1,
  paddingTop: HEADER.MOBILE_HEIGHT + 24,
  paddingBottom: isAuthenticated ? '74px' : '0px',
  background: '#130033',
  backgroundImage: `url(${require('../../assets/bg.webp')})`,
  backgroundSize: '100% 100%',
  // backgroundImage: `url(${require('../../assets/bg.png')})`,
  // backgroundSize: 'cover',
  // backgroundRepeat: 'no-repeat',
  position: 'relative',
  // zIndex: 0,
  [theme.breakpoints.only('xs')]: {
    paddingTop: HEADER.MOBILE_HEIGHT,
  },
  [theme.breakpoints.up('lg')]: {
    // paddingLeft: 8,
    // paddingRight: 8,
    paddingTop: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    // paddingBottom: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
    // ...(collapseClick && {
    //   marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    // }),
  },
}));

const BottomNav = styled(Box)(({ theme }) => ({
  borderTop: '2px solid white',
  borderBottom: '2px solid white',
  background: 'linear-gradient(180deg, #3F00C5, #0095E9)',
  // [theme.breakpoints.up('lg')]: {
  //   width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
  //   marginLeft: `calc(${NAVBAR.DASHBOARD_WIDTH}px)`,
  // },
  width: '100%',
  position: 'fixed',
  bottom: 0,
  left: 0,
  height: '74px',
  zIndex: 100,
  display: 'block',
  right: 0,
  // [theme.breakpoints.up('sm')]: {
  //   height: '90px',
  //   padding: '20px 20px 0 20px',
  // },
  // [theme.breakpoints.down('sm')]: {
  //   height: '70px',
  //   padding: '4px 4px 0 4px',
  // },
}));

const BottomNavGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexDirection: 'column',
  flex: 1,
}));

const BottomNavText = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontFamily: 'sans-serif',
  whiteSpace: 'nowrap',
  color: '#fff',
  fontWeight: 400,
  marginTop: 0,
}));

// ----------------------------------------------------------------------

const DashboardLayout = () => {
  const { isAuthenticated } = useAuth();

  const { collapseClick, isCollapse, onToggleCollapse } = useCollapseDrawer();

  const { isAddMemberBankDialog } = useSelector((x) => x.addMemberBankDialog);

  const { siteSettings, memberHasBanks } = useSelector((x) => x.lookup);

  const isVisible = usePageVisibility();

  const { translate } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { themeLayout } = useSettings();

  const isDesktop = useResponsive('up', 'lg');

  const [open, setOpen] = useState(false);

  const verticalLayout = themeLayout === 'vertical';

  const location = useLocation();

  useEffect(() => {
    const promises = [getGameCategsAndCompanies(), getSiteSettings()];

    dispatch(openLoadingScreen());
    Promise.all(promises).finally(() => dispatch(closeLoadingScreen()));
  }, []);

  /**
   * Call getUserMemberBalance() when tab is active and isAuthenticated
   * to keep the member balance updated
   */
  useEffect(() => {
    if (isVisible && isAuthenticated) getUserMemberBalance();
  }, [isVisible, isAuthenticated]);

  useEffect(() => {
    try {
      if (siteSettings?.config_analytics) {
        const _ga = siteSettings?.config_analytics?.split(',');

        // Multiple products (previously known as trackers)
        ReactGA.initialize(
          _ga.map((item) => ({
            // measurement ID
            trackingId: item,
            // gaOptions: {...}, // optional
            // gtagOptions: {...}, // optional
          }))
          //   [
          //   {
          //     // measurement ID
          //     trackingId: _ga[0],
          //     // gaOptions: {...}, // optional
          //     // gtagOptions: {...}, // optional
          //   },
          // ]
        );
      }

      // Comment out respond.io code as per Mike
      // if (siteSettings?.config_livechat) {
      //   const respondIo = JSON.parse(siteSettings?.config_livechat);
      //   const script = document.createElement('script');

      //   script.id = respondIo?.id;
      //   script.src = respondIo?.src;
      //   script.async = true;

      //   document.head.appendChild(script);
      // }

      if (siteSettings?.config_fb_pixel) {
        const metaPixel = siteSettings.config_fb_pixel.replace(/[^\d,]+/g, '').split(',');
        const options = {
          autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
          debug: false, // enable logs
        };

        metaPixel.forEach((_pixel) => {
          ReactPixel.init(_pixel, options);

          ReactPixel.pageView(); // For tracking page view
        });
      }
    } catch (error) {
      console.warn(`3RD PARTY INTEGRATION ERROR: ${error?.message}`);
    }
  }, [siteSettings]);

  const getAllGames = () => {
    conObj.post('games/all.json?limit=1').then((res) => {
      if (res.status === 200) {
        // res.data?.data?.data
        // set appversion to localstorage
        // res.data?.data.appversion
        const _allGames = res.data?.data?.data;

        dispatch(
          setGameCategories(
            _allGames
              .filter((f) => f.key.toLowerCase().indexOf('gcat') !== -1)
              .map((item) => ({ key: item.key, name: item.name, slug: item.slug }))
          )
        );
      }
    });
  };

  if (verticalLayout) {
    return (
      <>
        <DashboardHeader onOpenSidebar={() => setOpen(true)} verticalLayout={verticalLayout} />

        {/* {isDesktop ? (
          <NavbarHorizontal />
        ) : (
          <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
        )} */}

        <Box
          component="main"
          sx={{
            px: { lg: 2 },
            pt: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 80}px`,
            },
            pb: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 24}px`,
            },
          }}
        >
          <Outlet />
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
        // backgroundColor: '#0b1712',
      }}
    >
      <DashboardHeader
        isCollapse={isCollapse}
        isOpenSidebar={open}
        onOpenSidebar={() => setOpen(true)}
        onCloseSidebar={() => setOpen(!open)}
        onToggleCollapse={onToggleCollapse}
      />

      {isAuthenticated && <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />}

      <MainStyle isAuthenticated={isAuthenticated} collapseClick={collapseClick}>
        {/* <SubHeaderNav /> */}
        <Box
          sx={{
            // backgroundImage: `url(${require('../../assets/bg.png')})`,
            // backgroundSize: '100% 100%',
            // backgroundRepeat: 'no-repeat',
            width: '100%',
            // height: '100%',
            zIndex: -1,
            maxWidth: 992,
            mx: 'auto',
          }}
        >
          <Outlet />
          <SpinWheelButton
            sx={{
              position: 'fixed',
              right: '5px',
              top: { xs: '60%', sm: '60%', md: '65%' },
              // bottom: { xs: '80px', sm: '80px', md: '15%' },
              zIndex: 9,
            }}
          />
        </Box>

        <Footer />

        {/* BOTTOM NAVIGATION */}
        {isAuthenticated && (
          <BottomNav>
            <Grid
              container
              sx={{
                // backgroundColor: palette.bottomMenu.bgColor,
                // boxShadow: '0px 0px 8px 0px #FFD633',
                // borderRadius: '500px',
                height: `${NAVBAR.MOBILE_BOTTOM_HEIGHT}px`,
              }}
            >
              <BottomNavGrid onClick={() => setOpen(true)} item xs={3}>
                <Image src={require('../../assets/navbar/menu.webp')} alt="Menu" sx={{ width: 30, height: 30 }} />
                <BottomNavText>{translate('menu')}</BottomNavText>
              </BottomNavGrid>

              <BottomNavGrid
                onClick={() => {
                  if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
                  else dispatch(openTransactionsDialog({ open: true, isDeposit: true }));
                }}
                item
                xs={3}
              >
                <Image src={require('../../assets/navbar/deposit.webp')} alt="Slot" sx={{ width: 30, height: 30 }} />
                <BottomNavText>{translate('deposit')}</BottomNavText>
              </BottomNavGrid>

              <BottomNavGrid
                onClick={() => {
                  if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
                  else dispatch(openTransactionsDialog({ open: true, isDeposit: false }));
                }}
                item
                xs={3}
              >
                <Image src={require('../../assets/navbar/withdraw.webp')} alt="Slot" sx={{ width: 30, height: 30 }} />
                <BottomNavText>{translate('withdraw')}</BottomNavText>
              </BottomNavGrid>

              {/* VIP CLUB */}
              <Grid
                item
                xs={3}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexDirection: 'column',
                  flex: 1,
                }}
                onClick={() => {
                  navigate('/promotion');
                }}
              >
                <Image src={require('../../assets/navbar/promo.webp')} alt="Vip Club" sx={{ width: 30, height: 30 }} />
                <BottomNavText>{translate('promotions')}</BottomNavText>
              </Grid>
            </Grid>
          </BottomNav>
        )}
      </MainStyle>

      {siteSettings?.config_livechat && siteSettings?.config_livechat?.length === 8 ? (
        <LiveChatWidget
          license={siteSettings?.config_livechat}
          onReady={(data) => {
            const widgetContainer = document.getElementById('chat-widget-container');

            if (widgetContainer && data.state.visibility === 'minimized') {
              widgetContainer.classList.add('adjust-bottom-space');
            }
          }}
          onVisibilityChanged={(data) => {
            const widgetContainer = document.getElementById('chat-widget-container');

            if (widgetContainer) {
              if (data.visibility === 'maximized') {
                widgetContainer.classList.remove('adjust-bottom-space');
              } else widgetContainer.classList.add('adjust-bottom-space');
            }
          }}
        />
      ) : (
        <></>
      )}

      <TransactionDialog />

      {isAddMemberBankDialog?.open && <WithdrawFormDialog />}
    </Box>
  );
};

export default DashboardLayout;
